import { h, Fragment } from "preact";
import { useState } from "react";
import { Button, Input, Select, Typography } from "antd";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ServiceCard } from "./ServiceCard";
import { VlinkModal } from "./VlinkModal";
import axios from "axios";
import { useFlowServices } from "./useFlowServices.js";
import { collectData } from "./collectFlowData.js";
import { ServiceCardContent } from "./ServiceCardContent.jsx";

const { Option } = Select;

const TemplateSettings = ({ templateName, setTemplateName }) => (
  <div>
    <h3>General Flow Settings</h3>
    <h4>Template Name</h4>
    <Input
      placeholder="Enter template name"
      value={templateName}
      required
      onChange={(e) => setTemplateName(e.target.value)}
      style={{ marginBottom: 16 }}
    />
  </div>
);

const CreateFlowTemplateTab = () => {
  const [templateName, setTemplateName] = useState("");
  const [selectedLanguage] = useState("en");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [vlink, setVlink] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const {
    services,
    setServices,
    enabledServices,
    movingServiceId,
    direction,
    clearServices,
    toggleService,
    onDragEnd,
    moveService,
    activeServiceNumbers,
    updateInputParam,
    updateCheckbox,
    updateSelectParam,
    updateRadioGroup,
    updateChildCheckbox,
    updateCheckboxNested,
    updateSelectParamNested,
  } = useFlowServices(selectedLanguage);

  const clearAll = () => {
    clearServices();
    setTemplateName("");
    setError("");
  };

  const buildPostData = () => {
    const postData = { status_page: true, pipeline: [] };
    collectData(services, postData, {
      lang: selectedLanguage,
      withFiles: false,
    });
    return postData;
  };

  const sendRequest = async () => {
    setError("");
    const data = { template: buildPostData(), template_name: templateName };
    await axios
      .post("/user/flow/template", data)
      .then((res) => {
        setIsModalOpen(true);
        setVlink(res.data.vlink);
        clearAll();
      })
      .catch((err) => {
        setError(err?.response?.data.error_msg);
      });
  };

  const renderCardContent = (service) => (
    <ServiceCardContent
      service={service}
      setServices={setServices}
      onUpdateInput={updateInputParam}
      onUpdateCheckbox={updateCheckbox}
      onUpdateSelect={updateSelectParam}
      onUpdateRadioGroup={updateRadioGroup}
      onUpdateChildCheckbox={updateChildCheckbox}
      onCheckboxNested={updateCheckboxNested}
      onSelectNested={updateSelectParamNested}
    />
  );

  return (
    <>
      <div style={{ display: "flex", gap: "20px", flexDirection: "column", background: "white" }}>
        <div className="flows-tab-content">
          <div className="flows-two-col">
            <div style={{ flex: 1 }}>
              <h3>Create Flow Template</h3>
              <Typography.Text>
                <p>
                  Create reusable templates for your verification flows that can be easily applied
                  across multiple projects. Templates help standardize your verification processes
                  and save time by allowing you to define a set of services, their configuration,
                  and sequence just once. Once saved, these templates can be quickly applied to
                  new flows with minimal adjustments.
                </p>
              </Typography.Text>

              <h3>Services</h3>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="services">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {services.map((service, index) => {
                        const activeServiceNumber = activeServiceNumbers.find(
                          (n) => n.id === service.id
                        )?.number;
                        return (
                          <Draggable
                            key={service.id}
                            draggableId={String(service.id)}
                            index={index}
                            isDragDisabled={service.paramName === "consent"}
                            isDropDisabled={service.paramName === "consent"}
                          >
                            {(provided) => (
                              <div ref={provided.innerRef} {...provided.draggableProps}>
                                <ServiceCard
                                  service={service}
                                  services={services}
                                  moveService={moveService}
                                  toggleService={toggleService}
                                  renderCardContent={renderCardContent}
                                  provided={provided}
                                  index={index}
                                  activeServiceNumber={activeServiceNumber}
                                  movingServiceId={movingServiceId}
                                  direction={direction}
                                />
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {error && (
                <div style={{ paddingTop: "30px" }}>
                  <span style={{ color: "red" }}>{error}</span>
                </div>
              )}
              <div style={{ paddingTop: "30px" }}>
                <Button
                  disabled={enabledServices.length <= 0 || error?.length > 0 || isLoading || !templateName}
                  onClick={sendRequest}
                >
                  Create Template
                </Button>
                <Button style={{ marginLeft: "10px" }} onClick={clearAll}>
                  Clear all
                </Button>
              </div>
            </div>

            <div className="flow-settings" style={{ flex: 1 }}>
              <TemplateSettings templateName={templateName} setTemplateName={setTemplateName} />
            </div>
          </div>
        </div>

        {isModalOpen && (
          <VlinkModal
            vlink={vlink}
            setIsModalOpen={setIsModalOpen}
            isModalOpen={isModalOpen}
            setVlink={setVlink}
            mode="template"
          />
        )}
      </div>
    </>
  );
};

export default CreateFlowTemplateTab;
